/**
 * Shared constants for TheOddsAPI widgets.
 * Extracted from odds_dashboard_with_charts.html (lines 1184-1239).
 * Used by: SportsBrowserWidget, EventsBrowserWidget, OddsComparisonWidget, etc.
 */

// Sport type → emoji mapping (line 1184-1199)
export const SPORT_ICONS = {
  americanfootball: '\u{1F3C8}',
  baseball: '\u26BE',
  basketball: '\u{1F3C0}',
  boxing: '\u{1F94A}',
  cricket: '\u{1F3CF}',
  golf: '\u26F3',
  icehockey: '\u{1F3D2}',
  lacrosse: '\u{1F94D}',
  mma: '\u{1F94B}',
  rugbyleague: '\u{1F3C9}',
  rugbyunion: '\u{1F3C9}',
  soccer: '\u26BD',
  tennis: '\u{1F3BE}',
  aussierules: '\u{1F3C9}',
  politics: '\u{1F3DB}\uFE0F',
  default: '\u{1F3AF}',
}

/** Get emoji icon for a sport key (line 1202-1209) */
export function getSportIcon(sportKey) {
  if (!sportKey) return SPORT_ICONS.default
  for (const [key, icon] of Object.entries(SPORT_ICONS)) {
    if (key !== 'default' && sportKey.includes(key)) return icon
  }
  return SPORT_ICONS.default
}

// Bookmaker lists (lines 1212-1239)
export const US_BOOKMAKERS = [
  'draftkings', 'fanduel', 'betmgm', 'caesars', 'pointsbetus',
  'betrivers', 'unibet_us', 'williamhill_us', 'barstool', 'wynnbet',
  'foxbet', 'superbook', 'twinspires', 'betonlineag', 'bovada',
  'mybookieag', 'betus', 'lowvig', 'circasports',
]

export const UK_BOOKMAKERS = [
  'skybet', 'paddypower', 'williamhill', 'betfair', 'betfair_ex_uk',
  'ladbrokes', 'coral', 'betvictor', 'betway', 'betfred',
  'sport888', 'unibet_uk', 'matchbook', 'betfair_sb_uk', 'smarkets',
  'virginbet', 'livescorebet', 'mrgreen', 'betuk', 'spreadex',
  'boylesports', 'leovegas', 'casumo', 'grosvenor', 'betdaq',
]

export const EU_BOOKMAKERS = [
  'pinnacle', 'onexbet', 'marathon_bet', 'sport888', 'unibet_eu',
  'nordicbet', 'suprabets', 'betsson', 'betclic', 'everygame',
  'coolbet', 'betonlineag', 'bovada', 'mybookieag', 'intertops',
]

export const AU_BOOKMAKERS = [
  'sportsbet', 'ladbrokes_au', 'tab', 'pointsbetau', 'neds',
  'unibet', 'betfair_ex_au', 'bluebet', 'topbetta', 'betr_au',
  'playup',
]

export const EXCHANGE_BOOKMAKERS = [
  'betfair', 'betfair_ex_uk', 'betfair_ex_au', 'smarkets', 'matchbook', 'betdaq',
]

/** All bookmaker regions with their lists */
export const BOOKMAKER_REGIONS = {
  us: { label: 'US', bookmakers: US_BOOKMAKERS },
  us2: { label: 'US2', bookmakers: [] },
  uk: { label: 'UK', bookmakers: UK_BOOKMAKERS },
  eu: { label: 'EU', bookmakers: EU_BOOKMAKERS },
  au: { label: 'AU', bookmakers: AU_BOOKMAKERS },
}

/** Market types */
export const MARKET_TYPES = {
  h2h: 'Moneyline',
  spreads: 'Spreads',
  totals: 'Totals',
  outrights: 'Outrights',
}

// --- Bookmaker Logo Resolver ---

const BOOKMAKER_LOGO_MAP = {
  draftkings: 'draftkings.png',
  fanduel: 'fanduel.ico',
  betmgm: 'betmgm.ico',
  caesars: 'caesars.png',
  pointsbetus: 'pointsbet.svg',
  pointsbetau: 'pointsbet.svg',
  betrivers: 'betrivers.png',
  bovada: 'bovada.png',
  betonlineag: 'betonline.png',
  mybookieag: 'mybookie.png',
  pinnacle: 'pinnacle.svg',
  unibet_us: 'unibet.svg',
  unibet: 'unibet.svg',
  unibet_uk: 'unibet.svg',
  unibet_eu: 'unibet.svg',
  hardrock: 'hardrock.svg',
  fanatics: 'fanatics.png',
  fliff: 'fliff.png',
  ballybet: 'ballybet.png',
  thescore: 'thescore.svg',
  kalshi: 'kalshi.png',
  polymarket: 'polymarket.png',
  williamhill_us: 'caesars.png',
}

// Domain mapping for books without local logo files → Google favicon CDN
const BOOKMAKER_DOMAINS = {
  betus: 'betus.com.pa',
  lowvig: 'lowvig.ag',
  coral: 'coral.co.uk',
  ladbrokes: 'ladbrokes.com',
  ladbrokes_au: 'ladbrokes.com.au',
  nordicbet: 'nordicbet.com',
  betsson: 'betsson.com',
  neds: 'neds.com.au',
  tipico: 'tipico.de',
  tipico_de: 'tipico.de',
  winamax: 'winamax.fr',
  winamax_fr: 'winamax.fr',
  parionssport: 'enligne.parionssport.fdj.fr',
  sport888: '888sport.com',
  betr_au: 'betr.com.au',
  williamhill: 'williamhill.com',
  onexbet: '1xbet.com',
  coolbet: 'coolbet.com',
  smarkets: 'smarkets.com',
  tab: 'tab.com.au',
  sportsbet: 'sportsbet.com.au',
  paddypower: 'paddypower.com',
  espnbet: 'espnbet.com',
  betparx: 'betparx.com',
  virginbet: 'virginbet.com',
  livescorebet: 'livescorebet.com',
  leovegas: 'leovegas.com',
  grosvenor: 'grosvenorsport.com',
  casumo: 'casumo.com',
  tabtouch: 'tabtouch.com.au',
  playup: 'playup.com',
  betright: 'betright.com.au',
  gtbets: 'gtbets.eu',
  betanysports: 'betanysports.eu',
  betway: 'betway.com',
  boylesports: 'boylesports.com',
  betvictor: 'betvictor.com',
  marathon_bet: 'marathonbet.com',
  superbook: 'superbook.com',
  betfair: 'betfair.com',
  betfair_ex_uk: 'betfair.com',
  betfair_sb_uk: 'betfair.com',
  betfair_ex_au: 'betfair.com.au',
  betfred: 'betfred.com',
  bet365: 'bet365.com',
  foxbet: 'foxbet.com',
  wynnbet: 'wynnbet.com',
  twinspires: 'twinspires.com',
  barstool: 'barstoolsportsbook.com',
  matchbook: 'matchbook.com',
  mrgreen: 'mrgreen.com',
  betclic: 'betclic.fr',
  betuk: 'betuk.com',
  spreadex: 'spreadex.com',
  circasports: 'circasports.com',
  skybet: 'skybet.com',
  betdaq: 'betdaq.com',
  suprabets: 'suprabets.com',
  everygame: 'everygame.eu',
  bluebet: 'bluebet.com.au',
  topbetta: 'topbetta.com.au',
}

/** Get bookmaker logo path — local file first, then Google favicon CDN */
export function getBookmakerLogoPath(bookmakerKey) {
  if (!bookmakerKey) return null
  const key = bookmakerKey.toLowerCase()
  const file = BOOKMAKER_LOGO_MAP[key]
  if (file) return `/logos/books/${file}`

  // Fallback: Google favicon CDN
  const domain = BOOKMAKER_DOMAINS[key]
  if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`

  return null
}
